<template>
  <div>
    <b-row class="mb-1 align-items-center">
      <b-col cols="auto">
        <span class="mr-3">
          <strong>Total Items : </strong> {{ totalOutOfStockCount }}
        </span>
        <span class="mr-3">
          <strong>Total Amt : </strong> {{ formatCurrency(totalAmount) }}
        </span>
        <span>
          <strong>Availability : </strong> {{ availabilityPercentage }}%
        </span>
      </b-col>
      <b-col align="right">
        <b-button
          variant="outline-primary"
          class="mb-1 ml-2"
          :disabled="loading"
          @click="fetchItems"
        >
          Refresh
        </b-button>
        <b-button
          variant="outline-primary"
          class="ml-1 mb-1"
          :disabled="!(results.length > 0) || printing"
          @click="handlePrint"
        >
          <feather-icon
            v-if="!printing"
            icon="PrinterIcon"
            size="16"
          />
          <b-spinner
            v-if="printing"
            size="16"
            label="Small Spinner"
            variant="primary"
            small
          />
        </b-button>
      </b-col>
    </b-row>
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>
    <div class="table-responsive-wrapper">
      <b-table
        v-if="!loading && !loadingError"
        :items="results"
        :fields="tableFields"
        responsive="sm"
        striped
        hover
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        class="compact-table"
        no-local-sorting
        @sort-changed="sortingChanged"
      >
        <template #head(stock)="data">
          <span class="text-success">{{ data.label }}</span>
        </template>

        <template #cell(stock)="data">
          <span
            :class="{
              'text-danger': parseFloat(data.item.stock) === 0,
              'text-warning': parseFloat(data.item.stock) > 0 && parseFloat(data.item.stock) < 10,
              'text-success': parseFloat(data.item.stock) >= 10
            }"
          >
            {{ data.item.stock }}
          </span>
        </template>
        <template #cell(date)="data">
          {{ data.item.created_at ? formatedDate(data.item.created_at ,'DD-MM-YYYY LT') :'-' }}
        </template>
        <template #cell(total_amount)="data">
          {{ formatCurrency(data.item.quantity * data.item.rate) }}
        </template>
        <template #head(reminder)="data">
          <span
            v-b-tooltip.hover="{ boundary: 'window' }"
            title="Reminder"
          >{{ data.label }}</span>
        </template>
        <template #cell(reminder)="data">
          <feather-icon
            v-if="data.item.reminder_sent"
            v-b-tooltip.hover="{boundary:'window'}"
            title="Reminder Acknowledged"
            icon="BellIcon"
            size="18"
            class="text-success"
          />
          <feather-icon
            v-else-if="data.item.reminder_requested"
            v-b-tooltip.hover="{boundary:'window'}"
            title="Reminder Requested"
            icon="BellIcon"
            size="18"
            class="text-danger"
          />
          <span v-else>-</span>
        </template>

        <template #cell(action)="data">
          <feather-icon
            v-b-tooltip.hover="{boundary:'window'}"
            title="Edit Minimum Quantity"
            icon="EditIcon"
            size="20"
            class="cursor-pointer mr-1"
            @click="openEditShortageItemsForm(data.item.id)"
          />
        </template>
      </b-table>
    </div>

    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />
    <add-shortage-items
      v-if="showShortageItemsForm"
      :id="selectedShortageItemsId"
      @modal-closed="closeShortageItemsForm"
      @shortage-items-added="handleShortageItemsAdded"
      @shortage-items-updated="handleShortageItemsUpdated"
    />
  </div>
</template>

<script>
import {
  BSpinner, BTable, BAlert, BRow, BCol, BButton, VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import DetailedPagination from '../../UI/DetailedPagination.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BRow,
    BCol,
    BButton,
    BAlert,
    DetailedPagination,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      deactivating: false,
      loadingError: null,
      results: [],
      tableFields: [
        { key: 'product_name', label: 'Product Name', sortable: true },
        { key: 'company', label: 'Company', sortable: true },
        { key: 'quantity', label: 'Quantity', sortable: true },
        { key: 'stock', label: 'Stock(live)', sortable: true },
        { key: 'mrp', label: 'MRP', sortable: true },
        { key: 'total_amount', label: 'Total Amount', sortable: false },
        { key: 'buyer_identfier', label: 'Buyer', sortable: true },
        { key: 'created_by_name', label: 'Retailer', sortable: true },
        { key: 'date', label: 'Date', sortable: true },
        { key: 'reminder', label: 'Rem.', sortable: false },
        { key: 'action', label: 'Action', sortable: false },
      ],
      selectedShortageItemsId: null,
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      totalAmount: 0,
      totalOutOfStockCount: 0,
      allItemsCount: 0,
      availabilityPercentage: '0.00',
      sortBy: 'created_at',
      sortDesc: true,
      submitting: false,
      printing: false,
      showShortageItemsForm: false,
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },

    fetchItems() {
      this.loading = true
      axios.post('retailers/retailer_product_shortages_list',
        {
          page: this.currentPage,
          per_page: this.perPage,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
          filters: {
            ...this.filters,
          },
        })
        .then(res => {
          this.results = res.data.results
          this.totalOutOfStockCount = res.data.shortage_product_count
          this.totalAmount = res.data.total_shortage_value
          this.totalItems = res.data.count
          this.allItemsCount = res.data.all_shortage_product_count

          // Calculate availability percentage
          const soldCount = res.data.sold_product_count || 0
          const total = soldCount + this.allItemsCount
          if (!total || total === 0) {
            this.availabilityPercentage = '0.00'
          } else {
            const percentage = (soldCount / total) * 100
            this.availabilityPercentage = percentage.toFixed(2)
          }

          this.loadingError = null
          this.loading = false
        }).catch(error => {
          this.loadingError = error?.response?.data?.error || 'Something went wrong'
          this.loading = false
        })
    },

    // New method to fetch all items for printing
    fetchItemsForPrint() {
      return new Promise((resolve, reject) => {
        axios.post('retailers/retailer_product_shortages_list',
          {
            page: this.currentPage,
            per_page: 500,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc,
            filters: {
              ...this.filters,
            },
          })
          .then(res => {
            resolve(res.data.results)
          }).catch(error => {
            reject(error)
          })
      })
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return '-'
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2,
      }).format(value)
    },
    async handlePrint() {
      try {
        this.printing = true
        // Fetch all items for printing
        const printData = await this.fetchItemsForPrint()
        await this.printShortageReport(printData)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.message || 'Failed to print!',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.printing = false
      }
    },

    printShortageReport(printData) {
      return new Promise((resolve, reject) => {
        try {
          // Build summary section
          const summaryHTML = `
  <div style="margin-bottom: 20px; padding: 15px; background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px;">
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
      <h2 style="margin: 0; font-size: 20px;">Shortage Items Report</h2>
      <div style="font-size: 14px; font-weight: bold;">
        ${this.filters.start_date ? this.formatedDate(this.filters.start_date, 'DD-MM-YYYY') : '-'} to ${this.filters.end_date ? this.formatedDate(this.filters.end_date, 'DD-MM-YYYY') : '-'}
      </div>
    </div>
    <div style="display: flex; gap: 30px; flex-wrap: wrap;">
      <div><strong>Total Items:</strong> ${this.totalOutOfStockCount}</div>
      <div><strong>Total Amount:</strong> ${this.formatCurrency(this.totalAmount)}</div>
      <div><strong>Availability:</strong> ${this.availabilityPercentage}%</div>
    </div>
  </div>
`

          // Build table HTML with column width specifications
          let tableHTML = '<table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; table-layout: fixed;">'

          // Column group for width control
          tableHTML += '<colgroup>'
          tableHTML += '<col style="width: 50px;">' // S.No
          tableHTML += '<col style="width: 180px;">' // Item Name
          tableHTML += '<col style="width: 100px;">' // Company
          tableHTML += '<col style="width: 50px;">' // Qty
          tableHTML += '<col style="width: 80px;">' // Total
          tableHTML += '<col style="width: 100px;">' // Salesman
          tableHTML += '<col>' // Remark (takes remaining space)
          tableHTML += '</colgroup>'

          // Add header row
          tableHTML += '<thead><tr>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: center;">S.No</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: left;">Item Name</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: left;">Company</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: center;">Qty</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: right;">Total</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: left;">Salesman</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: left;">Remark</th>'
          tableHTML += '</tr></thead>'

          // Add data rows - Use printData instead of this.results
          tableHTML += '<tbody>'
          printData.forEach((item, index) => {
            const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f8f8'
            const totalAmount = (item.quantity && item.rate) ? (item.quantity * item.rate).toFixed(2) : '-'
            tableHTML += '<tr>'

            // S.No
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center;">${index + 1}</td>`

            // Item Name - wrap text if needed
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: left; word-wrap: break-word; overflow-wrap: break-word;">${item.product_name || '-'}</td>`

            // Company
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: left; word-wrap: break-word; overflow-wrap: break-word;">${item.company || '-'}</td>`

            // Quantity
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center;">${item.quantity || '-'}</td>`

            // Total Amount (Qty * Rate)
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: right;">${totalAmount}</td>`

            // Salesman
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: left; word-wrap: break-word; overflow-wrap: break-word;">${item.created_by_name || '-'}</td>`

            // Remark (blank) - takes remaining space
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: left;">&nbsp;</td>`

            tableHTML += '</tr>'
          })
          tableHTML += '</tbody></table>'

          // Create iframe for printing
          const iframe = document.createElement('iframe')
          iframe.style.position = 'absolute'
          iframe.style.top = '-9999px'
          iframe.style.left = '-9999px'
          document.body.appendChild(iframe)

          // Write content to iframe
          iframe.contentDocument.open()
          iframe.contentDocument.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              body {
                margin: 15px;
                font-family: Arial, sans-serif;
              }
              
              table {
                page-break-inside: auto;
              }
              
              tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
              
              th, td {
                page-break-inside: avoid;
              }
            }
            
            @page {
              margin: 0.5in;
              size: A4;
            }
            
            body {
              font-family: Arial, sans-serif;
              margin: 15px 5px;
            }
          </style>
        </head>
        <body>
          ${summaryHTML}
          ${tableHTML}
        </body>
        </html>
      `)
          iframe.contentDocument.close()

          setTimeout(() => {
            iframe.contentWindow.print()

            iframe.contentWindow.addEventListener('afterprint', () => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe)
              }
              resolve('Print completed successfully')
            })

            // Fallback cleanup
            setTimeout(() => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe)
                resolve('Print completed')
              }
            }, 500)
          }, 500)
        } catch (error) {
          reject(error)
        }
      })
    },
    openAddShortageItemsForm() {
      this.selectedShortageItemsId = null
      this.showShortageItemsForm = true
    },

    openEditShortageItemsForm(mimimimQuantityId) {
      this.selectedShortageItemsId = mimimimQuantityId
      this.showShortageItemsForm = true
    },

    closeShortageItemsForm() {
      this.showShortageItemsForm = false
      this.selectedShortageItemsId = null
    },

    handleShortageItemsAdded() {
      this.closeShortageItemsForm()
      this.currentPage = 1
      this.fetchItems()
    },

    handleShortageItemsUpdated() {
      this.closeShortageItemsForm()
      this.fetchItems()
    },
  },
}
</script>

<style scoped>
.table-responsive-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-responsive-wrapper .table {
  min-width: 100%;
  white-space: nowrap;
}
</style>
